import { Component, computed, OnDestroy, OnInit, signal } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { ActivatedRoute, Router } from "@angular/router";
import { paginatorConfig } from "@app/app.const";
import { FilterBarComponent } from "@components/filters/filter-bar/filter-bar.component";
import { DataTableComponent } from "@components/table/data-table/data-table.component";
import {
  TableAction,
  TableColumn,
  TableConfig,
  TableSortEvent,
} from "@components/table/models/table-config.model";
import { IOrderDetailEntity } from "@models/order.model";
import { AppStateService, RequestService } from "@services/index";
import { Subject, takeUntil } from "rxjs";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
  RefreshButtonComponent,
} from "../../../components";
import { CartSnapshotDialogComponent } from "./cart-snapshot-dialog.component";

@Component({
  selector: "fh-pending-order-list-page",
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    FormAutocompleteComponent,
    FilterBarComponent,
    DataTableComponent,
    ReactiveFormsModule,
    RefreshButtonComponent,
  ],
  templateUrl: "./pending-order-list-page.component.html",
  styleUrls: ["./pending-order-list-page.component.scss"],
})
export class PendingOrderListPageComponent implements OnInit, OnDestroy {
  /** Bound wrapper for the refresh button — see `loadPendingOrders()`. */
  refreshData = (): void => this.loadPendingOrders();
  // State signals
  iso2 = signal<string | null>(null);
  searchTerm = signal<string>("");
  pageIndex = signal<number>(0);
  pageSize = signal<number>(paginatorConfig.initialPageSize);
  sortProp = signal<string>("updated_on");
  sortDir = signal<"asc" | "desc">("desc");
  isLoading = signal<boolean>(false);

  // Data signals
  orders = signal<IOrderDetailEntity[]>([]);
  total = signal<number>(0);

  private readonly destroy$ = new Subject<void>();

  // Computed
  destinationCountries = computed(() => this.appStateService.destinationCountries());

  countryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    return this.destinationCountries().map((country) => ({
      value: country.iso2,
      label: country.name,
      count: 0,
    }));
  });

  countryFilterFormControl = new FormControl<string | null>(null);

  tableConfig = computed<TableConfig<IOrderDetailEntity>>(() => ({
    columns: this.getTableColumns(),
    data: this.orders(),
    total: this.total(),
    loading: this.isLoading(),
    pageSizeOptions: paginatorConfig.pageSizeOptions,
    initialPageSize: paginatorConfig.initialPageSize,
    showFirstLastButtons: true,
    stickyHeader: true,
    highlightRowOnClick: false,
    rowClickable: false,
  }));

  /** order_id currently being completed, to disable its row's action mid-request. */
  completingOrderId = signal<string | null>(null);

  tableActions = computed<TableAction<any>[]>(() => [
    {
      icon: "shopping_cart",
      label: "View Cart Snapshot",
      handler: (row) => this.viewCartSnapshot(row),
      color: "primary",
    },
    {
      icon: "task_alt",
      label: "Complete Order",
      handler: (row) => this.completeOrder(row),
      color: "primary",
      disabled: (row) => this.completingOrderId() === row.order_id,
    },
  ]);

  readonly paginatorConfig = paginatorConfig;

  constructor(
    private readonly requestService: RequestService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly appStateService: AppStateService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.pageIndex.set(Number(params.page) || 0);
      this.pageSize.set(Number(params.pageSize) || paginatorConfig.initialPageSize);
      this.searchTerm.set(params.search || "");
      this.iso2.set(params.iso2 || null);

      // for the first time when this page loads we need to patch value to form control
      this.countryFilterFormControl.patchValue(this.iso2() || null);

      this.loadPendingOrders();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCountryChange(iso2: string | null): void {
    this.updateQueryParams({ iso2: iso2 || undefined, page: 0 });
  }

  onSearchChange(searchTerm: string): void {
    this.updateQueryParams({ search: searchTerm || undefined, page: 0 });
  }

  onResetFilters(): void {
    this.updateQueryParams({
      iso2: undefined,
      search: undefined,
      page: 0,
    });
  }

  onSortChange(event: TableSortEvent): void {
    this.sortProp.set(event.prop);
    this.sortDir.set(event.dir as "asc" | "desc");
    this.loadPendingOrders();
  }

  onPageChange(event: PageEvent): void {
    this.updateQueryParams({
      page: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  private loadPendingOrders(): void {
    this.isLoading.set(true);

    let url = `/api/v1/pending_order_list?pageIndex=${this.pageIndex()}&pageSize=${this.pageSize()}`;

    if (this.iso2()) {
      url += `&iso2=${this.iso2()}`;
    }

    this.requestService.get(url).subscribe({
      next: (res: any) => {
        if (res.status) {
          this.total.set(parseInt(res.data.total, 10));
          this.orders.set(
            res.data.orders.map((order: any) => ({
              ...order,
              updated_on: this.appStateService.getFormattedDateFromISO({
                dateFormat: "yyyy-MM-dd HH:mm a",
                isoDateStr: order.updated_on,
              }),
            }))
          );
        } else {
          this.orders.set([]);
          this.total.set(0);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.orders.set([]);
        this.total.set(0);
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Recovery for an order stuck here despite the customer having actually paid —
   * e.g. HitPay/Stripe's webhook never arrived or failed signature verification.
   * Re-verifies payment status with the provider (server-side) before completing
   * anything, so this is safe to click on a row that's genuinely still unpaid —
   * it just reports nothing to do rather than fabricating an order.
   */
  completeOrder(row: any): void {
    if (
      !confirm(
        `Complete order ${row.order_id}?\n\n` +
          "This re-checks payment status with the provider and, if paid, creates the order and sends the order-received notification. Only use this if you've confirmed the payment actually succeeded (e.g. the provider's own dashboard shows it) but the order never appeared."
      )
    ) {
      return;
    }

    this.completingOrderId.set(row.order_id);
    this.requestService.post<any>(`/api/v2/pending-orders/${row.order_id}/complete`).subscribe({
      next: (res: any) => {
        this.completingOrderId.set(null);
        if (res.status && res.data?.orderCreated) {
          alert(`Order ${row.order_id} completed successfully.`);
          this.loadPendingOrders();
        } else if (res.status && res.data?.providerStatus === "not_paid") {
          alert(`Order ${row.order_id}: the provider still shows this as unpaid. Nothing to do.`);
        } else {
          alert(res.message || "Failed to complete order");
        }
      },
      error: (err) => {
        this.completingOrderId.set(null);
        alert(`Error completing order: ${err.error?.message || "Unknown error"}`);
      },
    });
  }

  viewCartSnapshot(row: any): void {
    this.dialog.open(CartSnapshotDialogComponent, {
      width: "900px",
      maxHeight: "90vh",
      data: row,
    });
  }

  private updateQueryParams(params: any): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: "merge",
    });
  }

  private getTableColumns(): TableColumn<IOrderDetailEntity>[] {
    return [
      {
        key: "order_id",
        header: "Order ID",
        sortable: false,
        width: "120px",
      },
      {
        key: "name",
        header: "Name",
        sortable: false,
        width: "180px",
      },
      {
        key: "shipping_email",
        header: "Email",
        sortable: false,
        width: "200px",
      },
      {
        key: "iso2",
        header: "Country",
        sortable: false,
        width: "100px",
      },
      {
        key: "currency",
        header: "Currency",
        sortable: false,
        width: "100px",
      },
      {
        key: "sub_total",
        header: "Subtotal",
        sortable: false,
        width: "120px",
        type: "number",
        format: (value) => `$${value}`,
      },
      {
        key: "shipping_total",
        header: "Shipping",
        sortable: false,
        width: "120px",
        type: "number",
        format: (value) => `$${value}`,
      },
      {
        key: "total_discount",
        header: "Discount",
        sortable: false,
        width: "120px",
        type: "number",
        format: (value) => `$${value}`,
      },
      {
        key: "total",
        header: "Total",
        sortable: false,
        width: "120px",
        type: "number",
        format: (value) => `$${value}`,
      },
      {
        key: "updated_on",
        header: "Updated",
        sortable: false,
        width: "180px",
      },
    ];
  }
}
